import React, { useState, useEffect } from 'react';
import api from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { Modal, Button, Form, Badge, Card, Alert, Tab, Tabs } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUsers, 
  faMusic, 
  faLock,
  faGlobe,
  faEdit,
  faTrash,
  faPlus,
  faInfoCircle,
  faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";

const CommunitiesManagement = () => {
    const [communities, setCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [debugInfo, setDebugInfo] = useState(null);
    const { csrfToken } = useAuth();

    // Modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedCommunity, setSelectedCommunity] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        visibility: 'Private',
        supported_event_types: '[]',
        allow_member_edits: false
    });
    
    // Stats
    const [communityStats, setCommunityStats] = useState({});

    const fetchCommunities = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // First, fetch all communities using the admin endpoint
            const response = await api.get('/gl_admin/communities');
            setCommunities(response.data);
            
            // For debugging, also fetch using the public endpoint
            try {
                const publicResponse = await api.get('/communities');
                
                // Compare the lists to identify visibility issues
                if (response.data && publicResponse.data && publicResponse.data.communities) {
                    const adminCommunityIds = new Set(response.data.map(c => c.id));
                    const publicCommunityIds = new Set(publicResponse.data.communities.map(c => c.id));
                    
                    const visibleToAdmin = [...adminCommunityIds].filter(id => !publicCommunityIds.has(id));
                    const visibleToPublic = [...publicCommunityIds].filter(id => !adminCommunityIds.has(id));
                    
                    setDebugInfo({
                        adminCount: response.data.length,
                        publicCount: publicResponse.data.communities.length,
                        visibleToAdminOnly: visibleToAdmin,
                        visibleToPublicOnly: visibleToPublic,
                        adminCommunities: response.data,
                        publicCommunities: publicResponse.data.communities
                    });
                }
            } catch (err) {
            }
            
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch communities: ' + (err.response?.data?.message || err.message));
            setLoading(false);
        }
    };
    
    const fetchCommunityStats = async () => {
        try {
            const response = await api.get('/gl_admin/communities/stats');
            const statsById = {};
            response.data.forEach(stat => {
                statsById[stat.community_id] = stat;
            });
            setCommunityStats(statsById);
        } catch (err) {
        }
    };

    useEffect(() => {
        fetchCommunities();
        fetchCommunityStats();
    }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            setFormData({
                ...formData,
                [name]: checked
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const handleEditClick = (community) => {
        setSelectedCommunity(community);
        
        // Parse supported_event_types if it's a string
        let supportedEventTypes = community.supported_event_types || '[]';
        if (typeof supportedEventTypes === 'string') {
            try {
                // Just keep it as a string for the form
                supportedEventTypes = community.supported_event_types;
            } catch (e) {
                supportedEventTypes = '[]';
            }
        } else if (Array.isArray(supportedEventTypes)) {
            // Convert array to JSON string
            supportedEventTypes = JSON.stringify(supportedEventTypes);
        }
        
        setFormData({
            name: community.name,
            description: community.description || '',
            visibility: community.visibility || 'Private',
            supported_event_types: supportedEventTypes,
            allow_member_edits: community.allow_member_edits || false
        });
        
        setShowEditModal(true);
    };
    
    const handleDetailsClick = (community) => {
        setSelectedCommunity(community);
        setShowDetailsModal(true);
    };

    const handleCreateClick = () => {
        setFormData({
            name: '',
            description: '',
            visibility: 'Private',
            supported_event_types: '[]',
            allow_member_edits: false
        });
        setShowCreateModal(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            // Log the form data being submitted
            
            if (selectedCommunity) {
                // Editing existing community
                const response = await api.put(`/gl_admin/communities/${selectedCommunity.id}`, formData, {
                    headers: { 'X-CSRFToken': csrfToken }
                });
            } else {
                // Creating new community
                const response = await api.post('/gl_admin/communities', formData, {
                    headers: { 'X-CSRFToken': csrfToken }
                });
            }
            setShowEditModal(false);
            setShowCreateModal(false);
            fetchCommunities();
        } catch (err) {
            setError('Failed to save community: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDeleteClick = async (communityId) => {
        // Check if community has users
        const stats = communityStats[communityId];
        if (stats && stats.user_count > 0) {
            if (!window.confirm(`This community has ${stats.user_count} users. Deleting it will remove these users from the community. Are you sure you want to proceed?`)) {
                return;
            }
        } else if (!window.confirm('Are you sure you want to delete this community? This action cannot be undone.')) {
            return;
        }
        
        try {
            const response = await api.delete(`/gl_admin/communities/${communityId}`, {
                headers: { 'X-CSRFToken': csrfToken }
            });
            fetchCommunities();
            fetchCommunityStats();
        } catch (err) {
            setError('Failed to delete community: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // Helper to render visibility badge
    const renderVisibilityBadge = (visibility) => {
        // Log the exact visibility value
        
        if (visibility === 'Private') {
            return (
                <Badge bg="secondary" className="me-2">
                    <FontAwesomeIcon icon={faLock} className="me-1" />
                    Private
                </Badge>
            );
        } else if (visibility === 'Public') {
            return (
                <Badge bg="info" className="me-2">
                    <FontAwesomeIcon icon={faGlobe} className="me-1" />
                    Public
                </Badge>
            );
        } else {
            // For any other cases, show the actual value
            return (
                <Badge bg="warning" className="me-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    {visibility ? visibility : 'Unknown'}
                </Badge>
            );
        }
    };

    if (loading) {
        return <div className="text-center mt-5"><div className="spinner-border" role="status"></div></div>;
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Communities Management</h2>
                <Button 
                    variant="primary" 
                    onClick={handleCreateClick}
                >
                    <FontAwesomeIcon icon={faPlus} className="me-1" /> Create Community
                </Button>
            </div>
            
            {error && <Alert variant="danger">{error}</Alert>}
            
            {debugInfo && (
                <Card className="mb-3 bg-light">
                    <Card.Header className="bg-info text-white">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Debug Information
                    </Card.Header>
                    <Card.Body>
                        <p className="mb-1"><strong>Admin API communities:</strong> {debugInfo.adminCount}</p>
                        <p className="mb-1"><strong>Public API communities:</strong> {debugInfo.publicCount}</p>
                        {debugInfo.visibleToAdminOnly.length > 0 && (
                            <p className="mb-1"><strong>Communities only visible to admin:</strong> {debugInfo.visibleToAdminOnly.join(', ')}</p>
                        )}
                        {debugInfo.visibleToPublicOnly.length > 0 && (
                            <p className="mb-1"><strong>Communities only visible publicly:</strong> {debugInfo.visibleToPublicOnly.join(', ')}</p>
                        )}
                        <div className="mt-3">
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={() => setDebugInfo(null)}
                            >
                                Hide Debug Info
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}
            
            <div className="table-responsive">
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Visibility</th>
                            <th>Editable</th>
                            <th>Description</th>
                            <th>Users</th>
                            <th>Event Types</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {communities.map(community => (
                            <tr key={community.id}>
                                <td>{community.id}</td>
                                <td>{community.name}</td>
                                <td>
                                    {renderVisibilityBadge(community.visibility)}
                                    <small className="d-block text-muted">
                                        Raw: '{community.visibility}'
                                    </small>
                                </td>
                                <td>
                                    <Badge bg={community.allow_member_edits ? "success" : "secondary"}>
                                        {community.allow_member_edits ? 'Yes' : 'No'}
                                    </Badge>
                                </td>
                                <td>
                                    {community.description ? 
                                        (community.description.length > 50 ? 
                                            `${community.description.substring(0, 50)}...` : 
                                            community.description) : 
                                        '-'}
                                </td>
                                <td>{communityStats[community.id]?.user_count || 0}</td>
                                <td>
                                    {(() => {
                                        try {
                                            const eventTypes = typeof community.supported_event_types === 'string' ? 
                                                JSON.parse(community.supported_event_types) : 
                                                community.supported_event_types || [];
                                            
                                            return eventTypes.length > 0 ? 
                                                `${eventTypes.length} types` : 
                                                'None';
                                        } catch (e) {
                                            return 'Error parsing';
                                        }
                                    })()}
                                </td>
                                <td>
                                    <Button 
                                        variant="info" 
                                        size="sm" 
                                        className="me-1"
                                        onClick={() => handleDetailsClick(community)}
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} />
                                    </Button>
                                    <Button 
                                        variant="primary" 
                                        size="sm" 
                                        className="me-1"
                                        onClick={() => handleEditClick(community)}
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </Button>
                                    <Button 
                                        variant="danger" 
                                        size="sm"
                                        onClick={() => handleDeleteClick(community.id)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Edit Community Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Community</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleFormSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Visibility</Form.Label>
                            <Form.Select
                                name="visibility"
                                value={formData.visibility}
                                onChange={handleInputChange}
                            >
                                <option value="Private">Private - Only visible to members</option>
                                <option value="Public">Public - Visible to all users</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Public communities are visible in the community list and can be joined by any user.
                                Private communities are only visible to members.
                            </Form.Text>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Supported Event Types (JSON format)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="supported_event_types"
                                value={formData.supported_event_types}
                                onChange={handleInputChange}
                            />
                            <Form.Text className="text-muted">
                                JSON array of event types. Example: ["rehearsal", "concert", "meeting"]
                            </Form.Text>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Allow members to edit community content"
                                name="allow_member_edits"
                                checked={formData.allow_member_edits}
                                onChange={handleInputChange}
                            />
                            <Form.Text className="text-muted">
                                If enabled, regular members can edit community content, not just admins.
                            </Form.Text>
                        </Form.Group>

                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" onClick={() => setShowEditModal(false)} className="me-2">
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit">
                                Save Changes
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Create Community Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Create New Community</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleFormSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Visibility</Form.Label>
                            <Form.Select
                                name="visibility"
                                value={formData.visibility}
                                onChange={handleInputChange}
                            >
                                <option value="Private">Private - Only visible to members</option>
                                <option value="Public">Public - Visible to all users</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Public communities are visible in the community list and can be joined by any user.
                                Private communities are only visible to members.
                            </Form.Text>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Supported Event Types (JSON format)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="supported_event_types"
                                value={formData.supported_event_types}
                                onChange={handleInputChange}
                            />
                            <Form.Text className="text-muted">
                                JSON array of event types. Example: ["rehearsal", "concert", "meeting"]
                            </Form.Text>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Allow members to edit community content"
                                name="allow_member_edits"
                                checked={formData.allow_member_edits}
                                onChange={handleInputChange}
                            />
                            <Form.Text className="text-muted">
                                If enabled, regular members can edit community content, not just admins.
                            </Form.Text>
                        </Form.Group>

                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" onClick={() => setShowCreateModal(false)} className="me-2">
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit">
                                Create Community
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
            
            {/* Community Details Modal */}
            <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Community Details: {selectedCommunity?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCommunity && (
                        <Tabs defaultActiveKey="basic" className="mb-3">
                            <Tab eventKey="basic" title="Basic Info">
                                <div className="row mb-2">
                                    <div className="col-4 fw-bold">ID:</div>
                                    <div className="col-8">{selectedCommunity.id}</div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-4 fw-bold">Name:</div>
                                    <div className="col-8">{selectedCommunity.name}</div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-4 fw-bold">Description:</div>
                                    <div className="col-8">{selectedCommunity.description || '-'}</div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-4 fw-bold">Visibility:</div>
                                    <div className="col-8">
                                        {renderVisibilityBadge(selectedCommunity.visibility)}
                                        <span className="ms-2 text-muted">Raw value: '{selectedCommunity.visibility}'</span>
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-4 fw-bold">Member Edits:</div>
                                    <div className="col-8">
                                        <Badge bg={selectedCommunity.allow_member_edits ? "success" : "secondary"}>
                                            {selectedCommunity.allow_member_edits ? 'Allowed' : 'Not Allowed'}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-4 fw-bold">Admin ID:</div>
                                    <div className="col-8">{selectedCommunity.admin_id || 'None'}</div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-4 fw-bold">Created:</div>
                                    <div className="col-8">
                                        {selectedCommunity.created_at ? new Date(selectedCommunity.created_at).toLocaleString() : 'Unknown'}
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-4 fw-bold">Last Updated:</div>
                                    <div className="col-8">
                                        {selectedCommunity.updated_at ? new Date(selectedCommunity.updated_at).toLocaleString() : 'Never'}
                                    </div>
                                </div>
                            </Tab>
                            <Tab eventKey="eventTypes" title="Event Types">
                                <div className="mb-3">
                                    <strong>Raw Value:</strong>
                                    <pre className="bg-light p-3 mt-2">{selectedCommunity.supported_event_types || 'null'}</pre>
                                </div>
                                
                                <div className="mb-3">
                                    <strong>Parsed Types:</strong>
                                    <div className="mt-2">
                                        {(() => {
                                            try {
                                                const eventTypes = typeof selectedCommunity.supported_event_types === 'string' ? 
                                                    JSON.parse(selectedCommunity.supported_event_types) : 
                                                    selectedCommunity.supported_event_types || [];
                                                
                                                return eventTypes.length > 0 ? (
                                                    <ul className="list-group">
                                                        {eventTypes.map((type, index) => (
                                                            <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                                                {type}
                                                                <Badge bg="primary" pill>{index + 1}</Badge>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <Alert variant="info">No event types defined</Alert>
                                                );
                                            } catch (e) {
                                                return (
                                                    <Alert variant="danger">
                                                        Error parsing event types: {e.message}
                                                    </Alert>
                                                );
                                            }
                                        })()}
                                    </div>
                                </div>
                            </Tab>
                            <Tab eventKey="debug" title="Debug">
                                <h5>Raw Community Object</h5>
                                <pre className="bg-light p-3 mb-3" style={{ maxHeight: '300px', overflow: 'auto' }}>
                                    {JSON.stringify(selectedCommunity, null, 2)}
                                </pre>
                                
                                {communityStats[selectedCommunity.id] && (
                                    <div>
                                        <h5>Community Stats</h5>
                                        <pre className="bg-light p-3">
                                            {JSON.stringify(communityStats[selectedCommunity.id], null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </Tab>
                        </Tabs>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                        Close
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => {
                            setShowDetailsModal(false);
                            handleEditClick(selectedCommunity);
                        }}
                    >
                        <FontAwesomeIcon icon={faEdit} className="me-1" />
                        Edit
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CommunitiesManagement;